/**
 * S-16 Conversation (V3a chat-fuse) — v3 "AFTER-DARK ARCADE" restyle.
 *
 * Romance register: warm, tactile, filmic. Bubbles carry weight (spring-in on
 * new messages only — history never replays), voice notes get a lit waveform,
 * reactions bloom from a glass pill, the composer is wet glass with crisp
 * tools. Safety surfaces (report/block/unsend/send-anyway) stay calm, serious
 * and untouched in behavior.
 *
 * Composer states follow the §3 opener matrix (server is the referee — this
 * screen only renders what /matches/{id} says): locked_waiting for the
 * rightless man, your_move + Opening-Move chip for the woman (incl. the EC-29
 * both-rights state), berserk_unlock badge for the Berserker man, expired
 * banner + Run It Back, closed state. Every 429 keeps the draft (§7.3
 * cap-hit table); EC-40's MATCH_EXPIRED keeps the draft and flips to the
 * expired state. Presentation + motion only: api/ws calls are byte-identical
 * to v2.
 *
 * SM-14 "Play together" (V10): server-personalized icebreaker chips in the
 * fresh-conversation empty state (one tap SENDS through the normal path, so
 * the §3 gate + #66 moderation apply), a pixel-gamepad Play tool in the
 * composer opening the game picker, and live game cards interleaved in the
 * thread (invite → board → result via the game.* WS pushes) — see games.tsx.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { copy, formatCopy, resolveCopy } from "@humble/shared";
import { AnimatePresence, motion } from "framer-motion";
import {
  Badge,
  Button,
  Card,
  Mascot,
  Sheet,
  Skeleton,
  springs,
  toast,
} from "../../design/index.js";
import { api, isApiError, withEventId } from "../../lib/api.js";
import { sessionStore, useStore } from "../../lib/store.js";
import { ws, wsCommand, WsCommandError } from "../../lib/ws.js";
import { Avatar, PresenceDot } from "../conversations/matches.js";
import {
  type GifWire,
  type MatchViewWire,
  type MessageViewWire,
  type PresenceBucket,
} from "../conversations/types.js";
import { fuseRemainingMs, formatRemaining, useNow } from "../conversations/use-now.js";
import { GifPicker } from "./gif-picker.js";
import {
  IconBolt,
  IconChevronLeft,
  IconDots,
  IconGif,
  IconImage,
  IconMic,
  IconPhone,
  IconSend,
} from "./icons.js";
import { useVoiceRecorder, VOICE_MAX_MS } from "./use-voice-recorder.js";
import { VoiceNote } from "./voice-note.js";
import {
  GamePickerSheet,
  GameThreadCard,
  PixelGamepad,
  useMatchGames,
  type GameWire,
} from "./games.js";
import "./chat.css";

type ComposerLock = { kind: "none" } | { kind: "wait_for_her" } | { kind: "opener_cap"; resetAt?: string };

interface SendBody {
  kind: "text" | "gif" | "image" | "voice";
  body?: string;
  gif_id?: string;
  media_id?: string;
  send_anyway?: boolean;
}

async function uploadChatMedia(kind: "chat_image" | "chat_voice", blob: Blob): Promise<string> {
  const form = new FormData();
  form.append("kind", kind);
  form.append("file", blob, kind === "chat_image" ? "image" : "voice-note");
  const token = sessionStore.get().accessToken;
  const res = await fetch("/api/v1/media", {
    method: "POST",
    headers: token ? { authorization: `Bearer ${token}` } : undefined,
    body: form,
    credentials: "include",
  });
  const data = (await res.json().catch(() => ({}))) as { id?: string; code?: string; message?: string };
  if (!res.ok || !data.id) throw new Error(data.message ?? `upload failed (${res.status})`);
  return data.id;
}

/** Live recording sketch — eight bars breathing while the mic is hot. */
function RecWave() {
  return (
    <span className="chat__rec-wave" aria-hidden="true">
      {Array.from({ length: 8 }, (_, i) => (
        <i key={i} style={{ animationDelay: `${i * 0.11}s` }} />
      ))}
    </span>
  );
}

export function ConversationScreen() {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const me = useStore(sessionStore, (s) => s.user);
  const myId = me?.id ?? "";
  const now = useNow(1000);

  const [match, setMatch] = useState<MatchViewWire | null>(null);
  const [gone, setGone] = useState(false);
  const [messages, setMessages] = useState<MessageViewWire[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [composerLock, setComposerLock] = useState<ComposerLock>({ kind: "none" });
  const [counterpartTyping, setCounterpartTyping] = useState(false);
  const [readSeq, setReadSeq] = useState<number | null>(null);
  const [presence, setPresence] = useState<PresenceBucket | null>(null);
  const [gifOpen, setGifOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pendingAnyway, setPendingAnyway] = useState<SendBody | null>(null);
  const [unsendTarget, setUnsendTarget] = useState<MessageViewWire | null>(null);
  const [actionsFor, setActionsFor] = useState<string | null>(null);
  const [mediaBusy, setMediaBusy] = useState(false);
  // SM-14 game layer state (games.tsx owns the transport + card rendering)
  const { games, upsertGame } = useMatchGames(matchId);
  const [gamePickerOpen, setGamePickerOpen] = useState(false);
  const [breakers, setBreakers] = useState<{ suggestions: string[]; can_send_now: boolean } | null>(null);
  const breakersFor = useRef<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const typingSentAt = useRef(0);
  const fileInput = useRef<HTMLInputElement>(null);
  const matchRef = useRef<MatchViewWire | null>(null);
  matchRef.current = match;
  // In-flight guard as a REF, not state: sendMediaWithRetry's loop keeps the
  // doSend closure it started with — a state-based guard captured `true`
  // forever when an image was picked while a text send was in flight, and the
  // media send hung until the retry budget ran out (composer stuck disabled).
  const sendingRef = useRef(false);
  const recorder = useVoiceRecorder();
  // Presentation-only: history renders still; only messages arriving after
  // the initial backfill settles get the spring-in entrance.
  const settledRef = useRef(false);

  const refreshMatch = useCallback(async () => {
    if (!matchId) return;
    try {
      const view = (await api.get("/matches/{matchId}", {
        params: { matchId },
      })) as unknown as MatchViewWire;
      setMatch(view);
      setPresence(view.counterpart_presence);
      setReadSeq(view.read_receipt_seq);
    } catch (err) {
      if (isApiError(err) && (err.status === 404 || err.status === 409)) setGone(true);
    }
  }, [matchId]);

  const backfill = useCallback(
    async (afterSeq: number) => {
      if (!matchId) return;
      try {
        const rows = (await api.get("/matches/{matchId}/messages", {
          params: { matchId },
          query: { after_seq: afterSeq, limit: 100 },
        })) as unknown as MessageViewWire[];
        if (rows.length > 0) {
          setMessages((prev) => {
            const known = new Set(prev.map((m) => m.id));
            return [...prev, ...rows.filter((r) => !known.has(r.id))].sort((a, b) => a.seq - b.seq);
          });
        }
      } catch {
        /* recovered on next refetch */
      }
    },
    [matchId],
  );

  // initial load + realtime subscriptions
  useEffect(() => {
    setMessages([]);
    settledRef.current = false;
    const settleTimer = window.setTimeout(() => {
      settledRef.current = true;
    }, 400);
    void refreshMatch();
    void backfill(0);
    ws.ensureConnected();
    if (!matchId) return () => window.clearTimeout(settleTimer);
    const offs = [
      ws.on("msg.new", (p) => {
        if (p.match_id !== matchId) return;
        const msg = p.message as unknown as MessageViewWire;
        setMessages((prev) =>
          prev.some((x) => x.id === msg.id) ? prev : [...prev, msg].sort((a, b) => a.seq - b.seq),
        );
        setCounterpartTyping(false);
        void refreshMatch();
      }),
      ws.on("msg.unsent", (p) => {
        if (p.match_id !== matchId) return;
        setMessages((prev) =>
          prev.map((m) =>
            m.id === p.message_id ? { ...m, unsent: true, body: undefined, gif: undefined, media_id: undefined } : m,
          ),
        );
      }),
      ws.on("msg.reaction", (p) => {
        if (p.match_id !== matchId) return;
        setMessages((prev) =>
          prev.map((m) => {
            if (m.id !== p.message_id) return m;
            const others = m.reactions.filter((r) => r.user_id !== p.user_id);
            return {
              ...m,
              reactions: p.emoji ? [...others, { user_id: p.user_id, emoji: p.emoji }] : others,
            };
          }),
        );
      }),
      ws.on("typing", (p) => {
        if (p.match_id !== matchId) return;
        setCounterpartTyping(p.on);
        if (p.on) window.setTimeout(() => setCounterpartTyping(false), 6000);
      }),
      ws.on("receipt.read", (p) => {
        if (p.match_id === matchId) setReadSeq(p.seq);
      }),
      ws.on("presence.update", (p) => {
        if (p.user_id === matchRef.current?.counterpart.id) setPresence(p.bucket);
      }),
      ws.on("match.expired", (p) => {
        if (p.match_id === matchId) void refreshMatch();
      }),
      ws.on("match.extended", (p) => {
        if (p.match_id === matchId) void refreshMatch();
      }),
      ws.on("match.unmatched", (p) => {
        if (p.match_id === matchId) setGone(true);
      }),
      ws.on("rib.offered", (p) => {
        if (p.match_id === matchId) void refreshMatch();
      }),
      ws.on("rib.reopened", () => void refreshMatch()),
    ];
    return () => {
      window.clearTimeout(settleTimer);
      offs.forEach((off) => off());
    };
  }, [matchId, refreshMatch, backfill]);

  // autoscroll + advance the read cursor (#50)
  const lastSeq = messages.length > 0 ? messages[messages.length - 1]!.seq : 0;
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
    if (!matchId || lastSeq === 0) return;
    void api
      .post("/matches/{matchId}/read", { params: { matchId }, body: { seq: lastSeq } })
      .catch(() => {});
  }, [matchId, lastSeq]);

  // SM-14: suggestions are per-match — drop them when the route moves on.
  useEffect(() => {
    setBreakers(null);
  }, [matchId]);

  // SM-14: personalized opener suggestions for a fresh conversation (fetched
  // once per match; the chips render even when the §3 gate says wait —
  // women-first — so the locked viewer can window-shop, not err).
  useEffect(() => {
    if (!matchId || !match) return;
    if (match.fuse.state === "expired" || (match.last_seq ?? 0) > 0 || messages.length > 0) return;
    if (breakersFor.current === matchId) return;
    breakersFor.current = matchId;
    void api
      .get("/matches/{matchId}/icebreakers", { params: { matchId } })
      .then((res) => {
        const view = res as unknown as { suggestions?: string[]; can_send_now?: boolean } | undefined;
        if (view) setBreakers({ suggestions: view.suggestions ?? [], can_send_now: Boolean(view.can_send_now) });
      })
      .catch(() => {
        /* the © fallback pools render instead */
      });
  }, [matchId, match, messages.length]);

  // keep the thread pinned to the newest beat when a game card lands/updates
  const gamesStamp = games.map((g) => `${g.game_id}:${g.updated_at}:${g.status}`).join("|");
  useEffect(() => {
    if (gamesStamp) listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [gamesStamp]);

  const unopened = (match?.last_seq ?? 0) === 0 && messages.length === 0;
  const expired = match?.fuse.state === "expired";
  const iCanSend = Boolean(match && !expired && (match.my_opener_rights || !unopened));
  const heteroLockedViewer = Boolean(match && unopened && !match.my_opener_rights);
  const berserkOpener = Boolean(match && unopened && match.my_opener_rights && match.fuse.whose_move === "both" && !match.opening_move_chip.visible);

  // --- send ------------------------------------------------------------------------------
  const doSend = useCallback(
    async (payload: SendBody): Promise<boolean> => {
      if (!matchId || sendingRef.current) return false; // busy → caller's retry loop re-attempts
      sendingRef.current = true;
      setSending(true);
      try {
        let sent: unknown;
        try {
          sent = await wsCommand("msg.send", { match_id: matchId, ...payload });
        } catch (err) {
          if (err instanceof WsCommandError && (err.code === "NOT_CONNECTED" || err.code === "ACK_TIMEOUT" || err.code === "SOCKET_CLOSED")) {
            // REST fallback twin — same guards, same idempotency semantics.
            sent = await api.post("/matches/{matchId}/messages", {
              params: { matchId },
              body: withEventId<SendBody & { client_event_id: string }>({ ...payload }) as never,
            });
          } else {
            throw err;
          }
        }
        const message = ((sent as { message?: MessageViewWire })?.message ?? sent) as MessageViewWire;
        if (message?.id) {
          setMessages((prev) =>
            prev.some((x) => x.id === message.id) ? prev : [...prev, message].sort((a, b) => a.seq - b.seq),
          );
        }
        void refreshMatch();
        return true;
      } catch (err) {
        const code = err instanceof WsCommandError ? err.code : isApiError(err) ? err.code : "INTERNAL";
        const msg = err instanceof Error ? err.message : "send failed";
        switch (code) {
          case "SEND_ANYWAY_REQUIRED":
            setPendingAnyway(payload); // #66b interstitial — dead serious
            break;
          case "MATCH_EXPIRED":
            toast(resolveCopy("chat.match_expired") ?? msg, "error");
            void refreshMatch(); // flips to the expired state; draft kept
            break;
          case "RATE_LIMITED":
            toast(resolveCopy("chat.rate_limited") ?? msg, "info"); // non-blocking, draft kept
            break;
          case "MEDIA_CAP":
            toast(resolveCopy("chat.media_cap_reached") ?? msg, "info");
            break;
          case "OPENER_CAP":
            setComposerLock({
              kind: "opener_cap",
              resetAt: isApiError(err) ? err.resetAt : undefined,
            });
            break;
          case "BERSERK_OVER":
            toast(resolveCopy("chat.berserk_over") ?? msg, "error");
            void refreshMatch();
            break;
          case "OPENER_RIGHTS":
            setComposerLock({ kind: "wait_for_her" });
            void refreshMatch();
            break;
          case "PAIR_CLOSED":
            setGone(true);
            break;
          case "MEDIA_NOT_READY":
            return false; // caller retries (held until scanned)
          default:
            toast(msg, "error");
        }
        return code === "MEDIA_NOT_READY" ? false : true;
      } finally {
        sendingRef.current = false;
        setSending(false);
      }
    },
    [matchId, refreshMatch],
  );

  const sendText = useCallback(
    async (text: string, sendAnyway = false) => {
      const body = text.trim();
      if (!body) return;
      const ok = await doSend({ kind: "text", body, send_anyway: sendAnyway || undefined });
      if (ok && !pendingAnyway) setDraft("");
      // NB: a 409/429 keeps the draft in the composer (normative — §7.3).
    },
    [doSend, pendingAnyway],
  );

  /** Held-until-scanned media send: retry MEDIA_NOT_READY while SM-13d scans. */
  const sendMediaWithRetry = useCallback(
    async (kind: "image" | "voice", mediaId: string) => {
      for (let attempt = 0; attempt < 20; attempt++) {
        const done = await doSend({ kind, media_id: mediaId });
        if (done) return;
        toast(resolveCopy("chat.media_pending") ?? "Scanning…", "info", 1500);
        await new Promise((r) => setTimeout(r, 2000));
      }
      toast(resolveCopy("moderation.image_pending") ?? "Still in review.", "error");
    },
    [doSend],
  );

  const onPickImage = useCallback(
    async (file: File) => {
      setMediaBusy(true);
      try {
        const id = await uploadChatMedia("chat_image", file);
        await sendMediaWithRetry("image", id);
      } catch (err) {
        toast(err instanceof Error ? err.message : "upload failed", "error");
      } finally {
        setMediaBusy(false);
      }
    },
    [sendMediaWithRetry],
  );

  const onVoiceStop = useCallback(async () => {
    const blob = await recorder.stop();
    if (!blob) return;
    setMediaBusy(true);
    try {
      const id = await uploadChatMedia("chat_voice", blob);
      await sendMediaWithRetry("voice", id);
    } catch (err) {
      toast(err instanceof Error ? err.message : "upload failed", "error");
    } finally {
      setMediaBusy(false);
    }
  }, [recorder, sendMediaWithRetry]);

  const onTyping = useCallback(() => {
    if (!matchId || Date.now() - typingSentAt.current < 3000) return;
    typingSentAt.current = Date.now();
    void wsCommand("msg.typing", { match_id: matchId, on: true }).catch(() => {});
  }, [matchId]);

  const reveal = useCallback(
    async (messageId: string) => {
      try {
        const res = await wsCommand<{ message: MessageViewWire }>("msg.reveal", { message_id: messageId });
        setMessages((prev) => prev.map((m) => (m.id === messageId ? res.message : m)));
      } catch {
        toast("Reveal needs a live connection.", "error");
      }
    },
    [],
  );

  const react = useCallback(
    async (message: MessageViewWire, emoji: string) => {
      const mine = message.reactions.find((r) => r.user_id === myId);
      try {
        if (mine?.emoji === emoji) {
          await api.delete("/messages/{messageId}/reactions", { params: { messageId: message.id } });
        } else {
          await api.put("/messages/{messageId}/reactions", {
            params: { messageId: message.id },
            body: { emoji },
          });
        }
      } catch (err) {
        if (isApiError(err)) toast(err.message, "error");
      }
      setActionsFor(null);
    },
    [myId],
  );

  const unsend = useCallback(async () => {
    if (!unsendTarget) return;
    try {
      await api.post("/messages/{messageId}/unsend", { params: { messageId: unsendTarget.id } });
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    }
    setUnsendTarget(null);
    setActionsFor(null);
  }, [unsendTarget]);

  const extend = useCallback(async () => {
    if (!matchId) return;
    try {
      const view = (await api.post("/matches/{matchId}/extend", {
        params: { matchId },
        body: withEventId({}) as never,
      })) as unknown as MatchViewWire;
      setMatch(view);
      toast(
        formatCopy(resolveCopy("chat.extend_done") ?? "+24 h", { n: view.fuse.extensions_used }),
        "success",
      );
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    }
  }, [matchId]);

  const runItBack = useCallback(async () => {
    if (!matchId) return;
    try {
      const view = (await api.post("/matches/{matchId}/run-it-back", {
        params: { matchId },
        body: withEventId({}) as never,
      })) as unknown as MatchViewWire;
      setMatch(view);
      if (view.fuse.state === "fuse_running") toast("Match reopened. Fresh fuse. Go.", "success");
      else toast(resolveCopy("chat.rib_waiting") ?? "Offer sent.", "success");
    } catch (err) {
      if (isApiError(err)) toast(err.message, err.code === "OFFER_ALREADY_LIVE" ? "info" : "error");
    }
  }, [matchId]);

  const toggleVideo = useCallback(async () => {
    if (!matchId || !match) return;
    const next = !match.video_optin.mine;
    try {
      await api.put("/matches/{matchId}/video-optin", { params: { matchId }, body: { enabled: next } });
      void refreshMatch();
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    }
  }, [matchId, match, refreshMatch]);

  const grantImageConsent = useCallback(async () => {
    if (!matchId) return;
    try {
      await api.put("/matches/{matchId}/image-consent", { params: { matchId }, body: { granted: true } });
      void refreshMatch();
      // Consent unlocks the recipient-side signed media_url — re-render the
      // history so already-delivered image bubbles pick up their URLs.
      setMessages([]);
      void backfill(0);
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    }
  }, [matchId, refreshMatch, backfill]);

  const doUnmatch = useCallback(async () => {
    if (!matchId) return;
    if (!window.confirm("Unmatch? This is permanent — you never see each other again.")) return;
    await api.delete("/matches/{matchId}", { params: { matchId } }).catch(() => {});
    navigate("/matches");
  }, [matchId, navigate]);

  const doBlock = useCallback(async () => {
    if (!match) return;
    if (!window.confirm("Block? Instant, permanent, and they are never told.")) return;
    await api
      .post("/blocks", { body: { user_id: match.counterpart.id } as never })
      .catch(() => {});
    navigate("/matches");
  }, [match, navigate]);

  const doReport = useCallback(async () => {
    if (!match) return;
    try {
      await api.post("/reports", {
        body: { category: "harassment", subject_user_id: match.counterpart.id } as never,
      });
      toast(resolveCopy("safety.report_ack") ?? "Report received.", "success");
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    }
    setMenuOpen(false);
  }, [match]);

  // --- derived render state -------------------------------------------------------------
  const openingMovePool = copy.chat.opening_move_default;
  // SM-14 icebreakers: personalized server suggestions first, © pools as the
  // fallback; can_send_now mirrors the §3 opener gate for render only.
  const fallbackBreakers = match?.type === "reject" ? copy.icebreakers.reject_pool : copy.icebreakers.classic_pool;
  const breakerLines = breakers && breakers.suggestions.length > 0 ? breakers.suggestions : [...fallbackBreakers];
  const canSendIcebreaker = breakers ? breakers.can_send_now : iCanSend;
  const partnerName = match?.counterpart.purged
    ? (resolveCopy("chat.tombstone") ?? "Deleted account")
    : (match?.counterpart.display_name ?? "…");
  const remaining = match ? fuseRemainingMs(match.fuse, now) : null;
  const lastOwnSeq = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]!.sender_id === myId) return messages[i]!.seq;
    }
    return null;
  }, [messages, myId]);
  // SM-14: game cards live IN the conversation. Messages keep their EXACT seq
  // order (unchanged from before); each game anchors after the last message
  // sent at-or-before its created_at, so cards read in conversational order.
  const thread = useMemo(() => {
    type ThreadItem = { key: string; anchor: number; at: string; msg?: MessageViewWire; game?: GameWire };
    const items: ThreadItem[] = messages.map((m) => ({
      key: `m-${m.id}`,
      anchor: m.seq,
      at: m.created_at,
      msg: m,
    }));
    for (const g of games) {
      let anchor = 0;
      for (const m of messages) {
        if (m.created_at <= g.created_at) anchor = m.seq;
      }
      items.push({ key: `g-${g.game_id}`, anchor, at: g.created_at, game: g });
    }
    return items.sort((a, b) => {
      if (a.anchor !== b.anchor) return a.anchor - b.anchor;
      if (Boolean(a.game) !== Boolean(b.game)) return a.game ? 1 : -1; // message first at its own seq
      if (a.at !== b.at) return a.at < b.at ? -1 : 1; // games between themselves
      return a.key < b.key ? -1 : 1;
    });
  }, [messages, games]);
  const liveGame = useMemo(
    () => games.find((g) => g.status === "invited" || g.status === "active") ?? null,
    [games],
  );

  if (gone) {
    return (
      <div className="chat chat--center">
        <Card padding="lg" className="chat__gone">
          <Mascot mood="ko" size={36} />
          <p className="text-dim">{resolveCopy("chat.closed")}</p>
          <Link to="/matches" className="btn" style={{ marginTop: 10 }}>
            {resolveCopy("chat.back_label") ?? "Back to matches"}
          </Link>
        </Card>
      </div>
    );
  }
  if (!match) {
    return (
      <div className="chat chat--center chat--loading" aria-busy="true">
        <div className="chat__skel" aria-hidden="true">
          <div className="chat__skel-row">
            <Skeleton width="52%" height={40} radius={16} />
          </div>
          <div className="chat__skel-row chat__skel-row--own">
            <Skeleton width="40%" height={40} radius={16} />
          </div>
          <div className="chat__skel-row">
            <Skeleton width="62%" height={56} radius={16} />
          </div>
        </div>
        <Mascot mood="loading" quip={resolveCopy("matches.loading_quip") ?? ""} />
      </div>
    );
  }

  return (
    <div className="chat">
      {/* header — wet glass, presence, the fuse as a lit timer */}
      <header className="chat__head glass">
        <Link
          to="/matches"
          className="chat__back"
          aria-label={resolveCopy("chat.back_label") ?? "Back to matches"}
        >
          <IconChevronLeft width={22} height={22} />
        </Link>
        <Avatar match={match} size={38} />
        <div className="chat__head-main">
          <span className="chat__name">
            {match.counterpart.purged
              ? (resolveCopy("chat.tombstone") ?? "Deleted account")
              : (match.counterpart.display_name ?? "…")}
            {berserkOpener && <Badge className="chat__berserk-badge">BERSERK</Badge>}
          </span>
          <span className="chat__sub text-faint">
            <PresenceDot bucket={presence ?? match.counterpart_presence} />
            {counterpartTyping
              ? (resolveCopy("chat.typing") ?? "typing…")
              : match.type === "reject"
                ? "Reject-Match"
                : ""}
          </span>
        </div>
        {match.fuse.state === "fuse_running" && remaining !== null && (
          <button className="chat__fuse" onClick={() => void extend()} title={resolveCopy("chat.extend_cta")}>
            <span
              className={`fuse-chip type-numeric ${remaining < 6 * 3600_000 && !match.fuse.frozen ? "fuse-chip--urgent" : ""} ${match.fuse.frozen ? "fuse-chip--frozen" : ""}`}
            >
              {match.fuse.frozen ? "⏸" : "⏱"} {formatRemaining(remaining)}
            </span>
          </button>
        )}
        {/* call buttons — SM-9 ships in V3b; the 3+3 + video-opt-in gates already render */}
        <button
          className="chat__call"
          disabled
          title={
            match.call_unlocked
              ? "Calls arrive with the next update."
              : (resolveCopy("chat.call_locked") ?? "")
          }
        >
          <IconPhone width={19} height={19} />
        </button>
        <button className="chat__menu-btn" aria-label="Conversation menu" onClick={() => setMenuOpen(true)}>
          <IconDots width={20} height={20} />
        </button>
      </header>

      {/* reject-match identity banner (requirement §2.5) */}
      {match.type === "reject" && (
        <div className="chat__reject-banner">
          <span className="type-editorial">{resolveCopy("match.reject_banner")}</span>
        </div>
      )}
      {match.fuse.frozen && (
        <div className="chat__frozen-note text-faint">Countdown paused. Not by you, not by them.</div>
      )}

      {/* expired state (EC-40 / SM-13b) */}
      {expired && (
        <Card padding="lg" className="chat__expired">
          <p className="chat__expired-kicker h-caps">{resolveCopy("matches.expired_title") ?? "Expired"}</p>
          <p className="text-dim">{resolveCopy("chat.match_expired")}</p>
          {match.rib.offered_by_them && <p className="type-editorial">{resolveCopy("chat.run_it_back")}</p>}
          <Button
            variant="primary"
            onClick={() => void runItBack()}
            disabled={match.rib.offered_by_me}
          >
            {match.rib.offered_by_me
              ? (resolveCopy("chat.rib_waiting") ?? "Waiting…")
              : match.rib.offered_by_them
                ? "Run it back — accept"
                : "Run it back"}
          </Button>
        </Card>
      )}

      {/* messages */}
      <div className="chat__list" ref={listRef}>
        {messages.length === 0 && games.length === 0 && !expired && (
          <div className="chat__empty">
            {match.type === "reject" ? (
              <p className="chat__empty-title type-display-3">{resolveCopy("match.reject_title")}</p>
            ) : (
              <Mascot mood="idle" size={34} />
            )}
            <p className="chat__empty-note type-editorial text-dim">
              {iCanSend ? resolveCopy("chat.your_move") : resolveCopy("chat.locked_waiting")}
            </p>
            {/* SM-14 icebreakers — one tap SENDS through the normal message
                path (§3 gate + #66 moderation apply on the actual send); when
                the gate says wait (women-first) the chips show but stay locked. */}
            <div className="chat__icebreakers" data-testid="icebreakers">
              <p className="chat__icebreakers-note text-faint">
                {resolveCopy(canSendIcebreaker ? "games.icebreaker_intro" : "games.icebreaker_wait")}
              </p>
              {breakerLines.slice(0, 5).map((line, i) => (
                <button
                  key={line}
                  className="chat__chip"
                  data-testid={`icebreaker-${i}`}
                  disabled={!canSendIcebreaker || sending}
                  onClick={() => void sendText(line)}
                >
                  {line}
                </button>
              ))}
            </div>
          </div>
        )}
        {thread.map((item) => {
          // SM-14: a game card — part of the conversation, hosted by Ticker.
          if (item.game) {
            return (
              <GameThreadCard
                key={item.key}
                game={item.game}
                myId={myId}
                partnerName={partnerName}
                onUpdate={upsertGame}
              />
            );
          }
          const m = item.msg!;
          const own = m.sender_id === myId;
          const seen = own && readSeq !== null && readSeq >= m.seq;
          const myReaction = m.reactions.find((r) => r.user_id === myId)?.emoji;
          return (
            <motion.div
              key={m.id}
              className={`bubble-row ${own ? "bubble-row--own" : ""}`}
              initial={settledRef.current ? { opacity: 0, y: 14, scale: 0.9 } : false}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={springs.bouncy}
              style={{ transformOrigin: own ? "bottom right" : "bottom left" }}
            >
              <div
                className={`bubble ${own ? "bubble--own" : ""} ${m.kind === "system" ? "bubble--system" : ""}`}
                onClick={() => setActionsFor(actionsFor === m.id ? null : m.id)}
              >
                {m.unsent ? (
                  <em className="text-faint">{resolveCopy("chat.message_removed")}</em>
                ) : m.folded ? (
                  <button
                    className="bubble__fold"
                    onClick={(e) => {
                      e.stopPropagation();
                      void reveal(m.id);
                    }}
                  >
                    {resolveCopy("chat.hidden_by_filters")}{" "}
                    <span className="text-faint">{resolveCopy("chat.tap_to_reveal") ?? "tap to reveal"}</span>
                  </button>
                ) : (
                  <>
                    {m.kind === "gif" && m.gif && <img className="bubble__gif" src={m.gif.url} alt={m.gif.title} />}
                    {m.kind === "image" &&
                      m.media_id &&
                      (own || match.image_consent.granted_by_me ? (
                        // Server-signed viewer-bound URL — a plain <img> cannot
                        // carry the bearer header, so the API sends media_url.
                        <img className="bubble__img" src={m.media_url} alt="" />
                      ) : (
                        <button
                          className="bubble__consent"
                          onClick={(e) => {
                            e.stopPropagation();
                            void grantImageConsent();
                          }}
                        >
                          <span className="bubble__consent-blur" aria-hidden="true" />
                          <span className="bubble__consent-text">
                            {formatCopy(resolveCopy("chat.image_consent") ?? "Show images from {name}?", {
                              name: match.counterpart.display_name ?? "them",
                            })}
                          </span>
                        </button>
                      ))}
                    {m.kind === "voice" && m.media_id && (
                      <VoiceNote className="bubble__voice" src={m.media_url} seed={m.id} />
                    )}
                    {m.body && <span className="bubble__text">{m.body}</span>}
                  </>
                )}
                {m.reactions.length > 0 && (
                  <motion.span
                    key={m.reactions.map((r) => r.emoji).join("")}
                    className="bubble__reactions"
                    initial={settledRef.current ? { scale: 0.6, opacity: 0 } : false}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={springs.bouncy}
                  >
                    {m.reactions.map((r) => (
                      <span key={r.user_id}>{r.emoji}</span>
                    ))}
                  </motion.span>
                )}
              </div>
              {seen && m.seq === lastOwnSeq && (
                <span className="bubble__seen text-faint">{resolveCopy("chat.seen") ?? "Seen"}</span>
              )}
              <AnimatePresence>
                {actionsFor === m.id && !m.unsent && (
                  <motion.div
                    className="bubble__actions glass"
                    initial={{ opacity: 0, scale: 0.8, y: 4 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.1 } }}
                    transition={springs.bouncy}
                  >
                    {["💀", "🔥", "😬", "❤️", "oof"].map((e) => (
                      <button
                        key={e}
                        className={`bubble__action ${myReaction === e ? "bubble__action--active" : ""}`}
                        onClick={() => void react(m, e)}
                      >
                        {e}
                      </button>
                    ))}
                    {own && (
                      <button className="bubble__action bubble__action--unsend" onClick={() => setUnsendTarget(m)}>
                        {resolveCopy("chat.unsend_label")}
                      </button>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
        {/* in-thread typing bubble — pure cosmetics; the header carries the a11y text */}
        {counterpartTyping && messages.length > 0 && (
          <motion.div
            className="bubble-row"
            aria-hidden="true"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springs.snappy}
          >
            <div className="bubble bubble--ghost">
              <span className="typing-dots">
                <i />
                <i />
                <i />
              </span>
            </div>
          </motion.div>
        )}
      </div>

      {/* composer */}
      {!expired &&
        (heteroLockedViewer || composerLock.kind === "wait_for_her" ? (
          <div className="chat__locked">
            <Mascot mood="idle" size={28} />
            <p className="type-editorial text-dim chat__locked-line">{resolveCopy("chat.locked_waiting")}</p>
            {match.fuse.whose_move && remaining !== null && (
              <p className="text-faint type-numeric chat__locked-timer">
                {formatCopy(resolveCopy("match.expiry_warning") ?? "", {
                  "hh:mm": formatRemaining(remaining),
                })}
              </p>
            )}
          </div>
        ) : composerLock.kind === "opener_cap" && unopened ? (
          <div className="chat__locked">
            <p className="text-dim">
              {formatCopy(resolveCopy("chat.opener_cap_reached") ?? "", {
                "hh:mm": composerLock.resetAt
                  ? formatRemaining(Math.max(0, new Date(composerLock.resetAt).getTime() - now))
                  : "--:--",
              })}
            </p>
          </div>
        ) : (
          <div className="chat__composer-zone glass">
            {match.opening_move_chip.visible && unopened && (
              <motion.button
                className="chat__opening-move"
                title={resolveCopy("profile.opening_move")}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={springs.bouncy}
                whileTap={{ scale: 0.96 }}
                onClick={() => {
                  // The chip previews which line it sends: her authored preset
                  // (S-25) or a system-pool line (§3 matrix).
                  const preset =
                    match.opening_move_chip.preset_source === "user"
                      ? ((me as { opening_move?: string } | null)?.opening_move ?? null)
                      : null;
                  const line = preset ?? openingMovePool[Math.floor(Math.random() * openingMovePool.length)]!;
                  void sendText(line);
                }}
              >
                <IconBolt width={14} height={14} /> Opening Move
                <span className="text-faint">
                  {match.opening_move_chip.preset_source === "user" ? " · your saved line" : " · house special"}
                </span>
              </motion.button>
            )}
            {berserkOpener && unopened && (
              <p className="chat__berserk-note">{resolveCopy("chat.berserk_unlock")}</p>
            )}
            {recorder.recording ? (
              <div className="chat__recording">
                <span className="chat__rec-dot" />
                <RecWave />
                <span className="chat__rec-clock type-numeric text-dim">
                  {Math.floor(recorder.elapsedMs / 1000)}s / {VOICE_MAX_MS / 1000}s
                </span>
                <Button size="sm" variant="ghost" onClick={() => recorder.cancel()}>
                  Cancel
                </Button>
                <Button size="sm" variant="primary" onClick={() => void onVoiceStop()}>
                  Send
                </Button>
              </div>
            ) : (
              <div className="chat__composer">
                {/* SM-14 Play — the pixel gamepad opens the game picker */}
                <button
                  className="chat__tool chat__tool--play"
                  aria-label={resolveCopy("games.play_hint") ?? "Play a game together"}
                  data-testid="game-play-btn"
                  onClick={() => setGamePickerOpen(true)}
                >
                  <PixelGamepad size={22} />
                </button>
                <button
                  className="chat__tool"
                  aria-label={resolveCopy("chat.tool_gif") ?? "Send a GIF"}
                  disabled={mediaBusy}
                  onClick={() => setGifOpen(true)}
                >
                  <IconGif width={20} height={20} />
                </button>
                <button
                  className="chat__tool"
                  aria-label={resolveCopy("chat.tool_image") ?? "Send an image"}
                  disabled={mediaBusy}
                  onClick={() => fileInput.current?.click()}
                >
                  <IconImage width={20} height={20} />
                </button>
                <button
                  className="chat__tool"
                  aria-label={resolveCopy("chat.tool_voice") ?? "Record a voice note"}
                  disabled={mediaBusy}
                  onClick={() => {
                    void recorder.start().then((ok) => {
                      if (!ok) toast(resolveCopy("call.perm_denied") ?? "Microphone refused.", "error");
                    });
                  }}
                >
                  <IconMic width={20} height={20} />
                </button>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) void onPickImage(f);
                  }}
                />
                <textarea
                  className="chat__input"
                  rows={1}
                  maxLength={2000}
                  placeholder={
                    unopened
                      ? (resolveCopy("chat.your_move") ?? "")
                      : (resolveCopy("chat.input_placeholder") ?? "Message")
                  }
                  value={draft}
                  onChange={(e) => {
                    setDraft(e.target.value);
                    onTyping();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void sendText(draft);
                    }
                  }}
                />
                <Button
                  variant="primary"
                  size="sm"
                  className="chat__send"
                  aria-label={resolveCopy("chat.send_label") ?? "Send"}
                  loading={sending}
                  disabled={!draft.trim()}
                  onClick={() => void sendText(draft)}
                >
                  {!sending && <IconSend width={18} height={18} />}
                </Button>
              </div>
            )}
          </div>
        ))}

      {/* GIF picker */}
      <GifPicker
        open={gifOpen}
        onClose={() => setGifOpen(false)}
        onPick={(gif: GifWire) => {
          setGifOpen(false);
          void doSend({ kind: "gif", gif_id: gif.id });
        }}
      />

      {/* SM-14 game picker — Ticker hosts; two_truths composes BEFORE the
          invite POSTs; opener-gate 403s land as friendly toasts (games.tsx) */}
      {matchId && (
        <GamePickerSheet
          open={gamePickerOpen}
          onClose={() => setGamePickerOpen(false)}
          matchId={matchId}
          hasLiveGame={liveGame !== null}
          onCreated={(g) => {
            upsertGame(g);
            setGamePickerOpen(false);
            toast(resolveCopy("games.invite_sent") ?? "Challenge sent.", "success");
          }}
        />
      )}

      {/* #66b send-anyway interstitial — dead serious, no humor */}
      <Sheet open={pendingAnyway !== null} onClose={() => setPendingAnyway(null)} title="Before you send">
        <p>{resolveCopy("chat.send_anyway")}</p>
        <div className="chat__sheet-row">
          <Button variant="ghost" onClick={() => setPendingAnyway(null)}>
            Edit message
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              const p = pendingAnyway;
              setPendingAnyway(null);
              if (p) {
                void doSend({ ...p, send_anyway: true }).then((ok) => {
                  if (ok) setDraft("");
                });
              }
            }}
          >
            Send anyway
          </Button>
        </div>
      </Sheet>

      {/* #58 unsend sheet — honest about already-delivered pushes */}
      <Sheet open={unsendTarget !== null} onClose={() => setUnsendTarget(null)} title="Unsend message">
        <p>{resolveCopy("chat.unsend_confirm")}</p>
        <div className="chat__sheet-row">
          <Button variant="ghost" onClick={() => setUnsendTarget(null)}>
            Keep it
          </Button>
          <Button variant="danger" onClick={() => void unsend()}>
            {resolveCopy("chat.unsend_label")}
          </Button>
        </div>
      </Sheet>

      {/* ⋯ menu */}
      <Sheet open={menuOpen} onClose={() => setMenuOpen(false)} title="This conversation">
        <div className="chat__menu">
          {match.fuse.state === "fuse_running" && (
            <button
              className="chat__menu-item"
              onClick={() => {
                setMenuOpen(false);
                void extend();
              }}
            >
              {resolveCopy("chat.extend_cta")}
              <span className="text-faint type-numeric"> · {match.fuse.extensions_used}/2 used</span>
            </button>
          )}
          {expired && (
            <button
              className="chat__menu-item"
              onClick={() => {
                setMenuOpen(false);
                void runItBack();
              }}
            >
              Run it back
            </button>
          )}
          <label className="chat__menu-item chat__menu-toggle">
            <span>
              Allow video calls in this chat
              <span className="chat__menu-sub text-faint">{resolveCopy("chat.video_optin")}</span>
            </span>
            <input type="checkbox" checked={match.video_optin.mine} onChange={() => void toggleVideo()} />
          </label>
          <hr className="chat__menu-rule" />
          {/* Safety controls — never gated, never joking (MISSION §2) */}
          <button className="chat__menu-item" onClick={() => void doReport()}>
            Report
          </button>
          <button className="chat__menu-item chat__menu-item--danger" onClick={() => void doBlock()}>
            Block
          </button>
          <button className="chat__menu-item chat__menu-item--danger" onClick={() => void doUnmatch()}>
            Unmatch
          </button>
        </div>
      </Sheet>
    </div>
  );
}
